import { isValidInAppRedirectUrl, isValidInAppRedirectTarget, NOTIFICATION_COUNT_SYNC_EVENTS, checkNotificationMatchesFilter } from './chunk-3UUYZMNX.mjs';
import { __spreadProps, __spreadValues, __objRest, __async } from './chunk-3LR3AF54.mjs';
import { createSignal, createEffect, onCleanup, createMemo, untrack, createRoot } from 'solid-js';
import clsx from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// src/ui/core/browser.ts
function isWebLocksSupported() {
  return typeof navigator !== "undefined" && "locks" in navigator && !!navigator.locks;
}
function requestLock(id, cb) {
  if (!isWebLocksSupported()) {
    cb(id);
    return () => {
    };
  }
  let isFulfilled = false;
  let promiseResolve;
  const promise = new Promise((resolve) => {
    promiseResolve = resolve;
  });
  navigator.locks.request(id, () => {
    if (!isFulfilled) {
      cb(id);
    }
    return promise;
  });
  return () => {
    isFulfilled = true;
    promiseResolve();
  };
}
var createNovuEventEffect = (novu, event, eventHandler) => {
  createEffect(() => {
    const cleanup = novu().on(event, eventHandler);
    onCleanup(() => {
      cleanup();
    });
  });
};
var createWebSocketEventEffect = (novu, webSocketEvent, onMessage) => {
  createEffect(() => {
    const currentNovu = novu();
    const channelName = `nv_ws_connection:a=${currentNovu.applicationIdentifier}:s=${currentNovu.subscriberId}:c=${currentNovu.contextKey}:e=${webSocketEvent}`;
    const tabsChannel = new BroadcastChannel(channelName);
    const listener = (event) => {
      onMessage(event.data);
    };
    tabsChannel.addEventListener("message", listener);
    const shouldBroadcast = isWebLocksSupported();
    const updateReadCount = (data) => {
      onMessage(data);
      if (shouldBroadcast) {
        tabsChannel.postMessage(data);
      }
    };
    let cleanup;
    const resolveLock = requestLock(channelName, () => {
      cleanup = currentNovu.on(webSocketEvent, updateReadCount);
    });
    onCleanup(() => {
      tabsChannel.removeEventListener("message", listener);
      tabsChannel.close();
      if (cleanup) {
        cleanup();
      }
      resolveLock();
    });
  });
};

// src/ui/core/format/normalizeIntlLocale.ts
function normalizeIntlLocale(locale) {
  return locale.replace(/_/g, "-");
}
var commonLocalization = {
  locale: "en-US"
};
var defaultInboxLocalization = __spreadProps(__spreadValues({}, commonLocalization), {
  "inbox.filters.dropdownOptions.unread": "Unread only",
  "inbox.filters.dropdownOptions.default": "Unread & read",
  "inbox.filters.dropdownOptions.archived": "Archived",
  "inbox.filters.dropdownOptions.snoozed": "Snoozed",
  "inbox.filters.labels.unread": "Unread",
  "inbox.filters.labels.default": "Inbox",
  "inbox.filters.labels.archived": "Archived",
  "inbox.filters.labels.snoozed": "Snoozed",
  "notifications.emptyNotice": "Quiet for now. Check back later.",
  "notifications.actions.readAll": "Mark all as read",
  "notifications.actions.archiveAll": "Archive all",
  "notifications.actions.archiveRead": "Archive read",
  "notifications.newNotifications": ({ notificationCount }) => `${notificationCount > 99 ? "99+" : notificationCount} new ${notificationCount === 1 ? "notification" : "notifications"}`,
  "notification.actions.read.tooltip": "Mark as read",
  "notification.actions.unread.tooltip": "Mark as unread",
  "notification.actions.archive.tooltip": "Archive",
  "notification.actions.unarchive.tooltip": "Unarchive",
  "notification.actions.snooze.tooltip": "Snooze",
  "notification.actions.unsnooze.tooltip": "Unsnooze",
  "notification.snoozedUntil": "Snoozed until",
  "preferences.title": "Preferences",
  "preferences.emptyNotice": "No notification specific preferences yet.",
  "preferences.global": "Global Preferences",
  "preferences.schedule.title": "Schedule",
  "preferences.schedule.description": "Allow notifications between:",
  "preferences.schedule.headerInfo": "Set your schedule. Notifications to external channels will pause outside the schedule. In-app and critical notifications are always delivered.",
  "preferences.schedule.info": "Critical and In-app notifications still reach you outside your schedule.",
  "preferences.schedule.days": "Days",
  "preferences.schedule.from": "From",
  "preferences.schedule.to": "To",
  "preferences.schedule.copyTimesTo": "Copy times to",
  "preferences.schedule.sunday": "Sunday",
  "preferences.schedule.monday": "Monday",
  "preferences.schedule.tuesday": "Tuesday",
  "preferences.schedule.wednesday": "Wednesday",
  "preferences.schedule.thursday": "Thursday",
  "preferences.schedule.friday": "Friday",
  "preferences.schedule.saturday": "Saturday",
  "preferences.schedule.dayScheduleCopy.title": "Copy times to:",
  "preferences.schedule.dayScheduleCopy.selectAll": "Select all",
  "preferences.schedule.dayScheduleCopy.apply": "Apply",
  "preferences.workflow.disabled.notice": "Contact admin to enable subscription management for this critical notification.",
  "preferences.workflow.disabled.tooltip": "Contact admin to edit",
  "preferences.group.info": "Applies to all notifications under this group.",
  "snooze.datePicker.timePickerLabel": "Time",
  "snooze.datePicker.apply": "Apply",
  "snooze.datePicker.cancel": "Cancel",
  "snooze.options.anHourFromNow": "An hour from now",
  "snooze.datePicker.pastDateTooltip": "Selected time must be at least 3 minutes in the future",
  "snooze.datePicker.noDateSelectedTooltip": "Please select a date",
  "snooze.datePicker.exceedingLimitTooltip": ({ days }) => `Selected time cannot exceed ${days === 1 ? "24 hours" : `${days} days`} from now`,
  "snooze.options.customTime": "Custom time...",
  "snooze.options.inOneDay": "Tomorrow",
  "snooze.options.inOneWeek": "Next week"
});
var defaultSubscriptionLocalization = __spreadProps(__spreadValues({}, commonLocalization), {
  "subscription.subscribe": "Subscribe",
  "subscription.unsubscribe": "Unsubscribe",
  "subscription.preferences.header": "Manage subscription",
  "subscription.preferences.headerInfo": "Manage which updates you\u2019d like to receive. Note: Workflow and global settings control delivery and take precedence when disabled.",
  "subscription.preferences.notSubscribed.header": "You\u2019re not subscribed.",
  "subscription.preferences.notSubscribed.description": "Subscribe to receive updates on new activity.",
  "subscription.preferences.empty.header": "You\u2019re subscribed.",
  "subscription.preferences.empty.description": "Nothing to manage right now."
});
var defaultLocalization = __spreadValues(__spreadValues({}, defaultInboxLocalization), defaultSubscriptionLocalization);
var [dynamicLocalization, setDynamicLocalization] = createSignal({});

// src/ui/core/stores/localization.ts
var createLocalizationStore = (localization) => {
  const dictionary = createMemo(() => {
    const _a = localization() || {}, { dynamic } = _a, localizationObject = __objRest(_a, ["dynamic"]);
    return __spreadValues(__spreadValues(__spreadValues(__spreadValues({}, defaultLocalization), dynamicLocalization()), dynamic || {}), localizationObject);
  });
  const t = (key, ...args) => {
    const value = dictionary()[key];
    if (typeof value === "function") {
      return value(args[0]);
    }
    return value;
  };
  const locale = createMemo(() => normalizeIntlLocale(dictionary().locale));
  return { t, locale, dictionary };
};
var DEFAULT_TARGET = "_blank";
var DEFAULT_REFERRER = "noopener noreferrer";
var KEYLESS_APPLICATION_IDENTIFIER_PREFIX = "pk_keyless_";
var DEFAULT_LIMIT = 10;
var STATUS_TO_FILTER = {
  ["unreadRead" /* UNREAD_READ */]: { archived: false, snoozed: false },
  ["unread" /* UNREAD */]: { read: false, snoozed: false },
  ["archived" /* ARCHIVED */]: { archived: true },
  ["snoozed" /* SNOOZED */]: { snoozed: true }
};
var getTagsFromTab = (tab) => {
  var _a;
  return ((_a = tab == null ? void 0 : tab.filter) == null ? void 0 : _a.tags) || (tab == null ? void 0 : tab.value) || [];
};
function isKeylessApplicationIdentifier(applicationIdentifier) {
  return !!(applicationIdentifier == null ? void 0 : applicationIdentifier.startsWith(KEYLESS_APPLICATION_IDENTIFIER_PREFIX));
}
var createNavigate = (routerPush) => {
  return (url, target) => {
    if (!url || !isValidInAppRedirectUrl(url)) {
      return;
    }
    const isAbsoluteUrl = !url.startsWith("/");
    if (isAbsoluteUrl) {
      const safeTarget = isValidInAppRedirectTarget(target) ? target : DEFAULT_TARGET;
      window.open(url, safeTarget, DEFAULT_REFERRER);
      return;
    }
    const push = routerPush();
    if (push) {
      push(url);
      return;
    }
    const fullUrl = new URL(url, window.location.href);
    const pushState = window.history.pushState.bind(window.history);
    pushState({}, "", fullUrl);
  };
};
var createInboxStore = (args) => {
  var _a, _b, _c, _d;
  const initialTabs = args.tabs();
  const [isOpened, setIsOpened] = createSignal(false);
  const [tabs, setTabs] = createSignal(initialTabs);
  const [activeTab, setActiveTab] = createSignal((_b = (_a = initialTabs[0]) == null ? void 0 : _a.label) != null ? _b : "");
  const [status, setStatus] = createSignal("unreadRead" /* UNREAD_READ */);
  const [limit, setLimit] = createSignal(DEFAULT_LIMIT);
  const [filter, setFilter] = createSignal(__spreadProps(__spreadValues({}, STATUS_TO_FILTER["unreadRead" /* UNREAD_READ */]), {
    tags: initialTabs.length > 0 ? getTagsFromTab(initialTabs[0]) : [],
    data: initialTabs.length > 0 ? (_c = initialTabs[0].filter) == null ? void 0 : _c.data : {},
    severity: initialTabs.length > 0 ? (_d = initialTabs[0].filter) == null ? void 0 : _d.severity : void 0
  }));
  const [hideBranding, setHideBranding] = createSignal(false);
  const [isDevelopmentMode, setIsDevelopmentMode] = createSignal(false);
  const [maxSnoozeDurationHours, setMaxSnoozeDurationHours] = createSignal(0);
  const isSnoozeEnabled = createMemo(() => maxSnoozeDurationHours() > 0);
  const [preferencesFilter, setPreferencesFilter] = createSignal(
    args.preferencesFilter()
  );
  const [isKeyless, setIsKeyless] = createSignal(false);
  const [applicationIdentifier, setApplicationIdentifier] = createSignal(null);
  const [contextKeys, setContextKeys] = createSignal(void 0);
  const [preferenceGroups, setPreferenceGroups] = createSignal(args.preferenceGroups());
  const [preferencesSort, setPreferencesSort] = createSignal(args.preferencesSort());
  const setNewStatus = (newStatus) => {
    setStatus(newStatus);
    setFilter((old) => __spreadProps(__spreadValues({}, STATUS_TO_FILTER[newStatus]), { tags: old.tags, data: old.data, severity: old.severity }));
  };
  const setNewActiveTab = (newActiveTab) => {
    const tab = tabs().find((tab2) => tab2.label === newActiveTab);
    const tags = getTagsFromTab(tab);
    if (!tags) {
      return;
    }
    setActiveTab(newActiveTab);
    setFilter((old) => {
      var _a2, _b2;
      return __spreadProps(__spreadValues({}, old), { tags, data: (_a2 = tab == null ? void 0 : tab.filter) == null ? void 0 : _a2.data, severity: (_b2 = tab == null ? void 0 : tab.filter) == null ? void 0 : _b2.severity });
    });
  };
  const navigate = createNavigate(args.routerPush);
  createEffect(() => {
    var _a2;
    const nextTabs = args.tabs();
    setTabs(nextTabs);
    const firstTab = nextTabs[0];
    const tags = getTagsFromTab(firstTab);
    setActiveTab((_a2 = firstTab == null ? void 0 : firstTab.label) != null ? _a2 : "");
    setFilter((old) => {
      var _a3, _b2;
      return __spreadProps(__spreadValues({}, old), { tags, data: (_a3 = firstTab == null ? void 0 : firstTab.filter) == null ? void 0 : _a3.data, severity: (_b2 = firstTab == null ? void 0 : firstTab.filter) == null ? void 0 : _b2.severity });
    });
    setPreferencesFilter(args.preferencesFilter());
    setPreferenceGroups(args.preferenceGroups());
  });
  createEffect(() => {
    setPreferencesSort(() => args.preferencesSort());
  });
  createNovuEventEffect(args.novu, "session.initialize.resolved", ({ data }) => {
    var _a2;
    if (!data) {
      return;
    }
    const storedKeylessIdentifier = window.localStorage.getItem("novu_keyless_application_identifier");
    setHideBranding(data.removeNovuBranding);
    setIsDevelopmentMode(data.isDevelopmentMode);
    setMaxSnoozeDurationHours(data.maxSnoozeDurationHours);
    setContextKeys(data.contextKeys);
    const configuredIdentifier = args.applicationIdentifier();
    if (!configuredIdentifier) {
      const keylessActive = isKeylessApplicationIdentifier(data.applicationIdentifier) || isKeylessApplicationIdentifier(storedKeylessIdentifier);
      setIsKeyless(keylessActive);
      setApplicationIdentifier((_a2 = data.applicationIdentifier) != null ? _a2 : null);
    } else {
      setIsKeyless(false);
      setApplicationIdentifier(configuredIdentifier);
    }
  });
  return {
    status,
    setStatus: setNewStatus,
    filter,
    tabs,
    activeTab,
    setActiveTab: setNewActiveTab,
    limit,
    setLimit,
    isOpened,
    setIsOpened,
    navigate,
    hideBranding,
    preferencesFilter,
    preferenceGroups,
    preferencesSort,
    isDevelopmentMode,
    maxSnoozeDurationHours,
    isSnoozeEnabled,
    isKeyless,
    applicationIdentifier,
    contextKeys
  };
};
var MIN_AMOUNT_OF_NOTIFICATIONS = 1;
var createCountsKey = (filter) => {
  var _a, _b;
  return JSON.stringify({ tags: (_a = filter.tags) != null ? _a : [], data: (_b = filter.data) != null ? _b : {}, severity: filter.severity });
};
var emptySeverityCounts = () => ({
  ["high" /* HIGH */]: 0,
  ["medium" /* MEDIUM */]: 0,
  ["low" /* LOW */]: 0,
  ["none" /* NONE */]: 0
});
var createCountsStore = ({ novu, inbox }) => {
  const [unreadCount, setUnreadCount] = createSignal({ total: 0, severity: emptySeverityCounts() });
  const [unreadCounts, setUnreadCounts] = createSignal(/* @__PURE__ */ new Map());
  const [newNotificationCounts, setNewNotificationCounts] = createSignal(/* @__PURE__ */ new Map());
  let refreshGeneration = 0;
  let subscribers = 0;
  let dispose;
  const refreshCounts = () => __async(null, null, function* () {
    const generation = ++refreshGeneration;
    const client = novu();
    const bellFilters = [
      "high" /* HIGH */,
      "medium" /* MEDIUM */,
      "low" /* LOW */,
      "none" /* NONE */
    ].map((severity) => ({
      read: false,
      archived: false,
      snoozed: false,
      severity
    }));
    const currentTabs = inbox.tabs();
    const tabFilters = currentTabs.length > 0 ? currentTabs.map((tab) => {
      var _a, _b;
      return {
        tags: getTagsFromTab(tab),
        read: false,
        archived: false,
        snoozed: false,
        data: (_a = tab.filter) == null ? void 0 : _a.data,
        severity: (_b = tab.filter) == null ? void 0 : _b.severity
      };
    }) : null;
    const [bellResult, tabResult] = yield Promise.all([
      client.notifications.count({ filters: bellFilters }),
      tabFilters ? client.notifications.count({ filters: tabFilters }) : Promise.resolve(null)
    ]);
    if (generation !== refreshGeneration) {
      return;
    }
    if (bellResult.data) {
      const severity = emptySeverityCounts();
      let total = 0;
      for (const item of bellResult.data.counts) {
        const filterSeverity = item.filter.severity;
        const severityKey = Array.isArray(filterSeverity) ? filterSeverity[0] : filterSeverity;
        if (severityKey && severityKey in severity) {
          severity[severityKey] = item.count;
          total += item.count;
        }
      }
      setUnreadCount({ total, severity });
    }
    if (tabResult == null ? void 0 : tabResult.data) {
      const newMap = /* @__PURE__ */ new Map();
      for (let i = 0; i < tabResult.data.counts.length; i += 1) {
        const countItem = tabResult.data.counts[i];
        const tagsKey = createCountsKey({
          tags: countItem.filter.tags,
          data: countItem.filter.data,
          severity: countItem.filter.severity
        });
        newMap.set(tagsKey, countItem.count);
      }
      setUnreadCounts(newMap);
    }
  });
  const updateNewNotificationCountsOrCache = (tabLabel, notification, tags, data, severity) => {
    const notificationsCache = novu().notifications.cache;
    const limitValue = inbox.limit();
    const tabSpecificFilterForCache = __spreadProps(__spreadValues({}, inbox.filter()), { tags, data, severity, after: void 0, limit: limitValue });
    const hasEmptyCache = !notificationsCache.has(tabSpecificFilterForCache);
    if (hasEmptyCache && (!inbox.isOpened() || inbox.activeTab() !== tabLabel)) {
      return;
    }
    const cachedData = notificationsCache.getAll(tabSpecificFilterForCache) || {
      hasMore: false,
      filter: tabSpecificFilterForCache,
      notifications: []
    };
    const hasLessThenMinAmount = ((cachedData == null ? void 0 : cachedData.notifications.length) || 0) < MIN_AMOUNT_OF_NOTIFICATIONS;
    if (hasLessThenMinAmount || !inbox.isOpened()) {
      notificationsCache.update(tabSpecificFilterForCache, __spreadProps(__spreadValues({}, cachedData), {
        notifications: [notification, ...cachedData.notifications]
      }));
      return;
    }
    setNewNotificationCounts((oldMap) => {
      const key = createCountsKey({ tags, data, severity });
      const newMap = new Map(oldMap);
      newMap.set(key, (oldMap.get(key) || 0) + 1);
      return newMap;
    });
  };
  const start = () => createRoot((disposeRoot) => {
    createEffect(() => {
      novu();
      refreshCounts();
    });
    createWebSocketEventEffect(novu, "notifications.unread_count_changed", (data) => {
      setUnreadCount(data.result);
      refreshCounts();
    });
    createEffect(() => {
      const client = novu();
      const cleanups = NOTIFICATION_COUNT_SYNC_EVENTS.map(
        (event) => client.on(event, (payload) => {
          if ("error" in payload && payload.error) {
            return;
          }
          refreshCounts();
        })
      );
      onCleanup(() => cleanups.forEach((cleanup) => cleanup()));
    });
    createNovuEventEffect(novu, "session.initialize.resolved", ({ data }) => {
      if (!data) {
        return;
      }
      setUnreadCount(data.unreadCount);
    });
    createWebSocketEventEffect(novu, "notifications.notification_received", (_0) => __async(null, [_0], function* ({ result: notification }) {
      var _a, _b, _c, _d, _e, _f;
      if (inbox.filter().archived || inbox.filter().snoozed) {
        return;
      }
      const currentTabs = inbox.tabs();
      const processedFilters = /* @__PURE__ */ new Set();
      if (currentTabs.length > 0) {
        for (const tab of currentTabs) {
          const tabTags = getTagsFromTab(tab);
          const tabFilter = {
            tags: tabTags,
            read: false,
            archived: false,
            snoozed: false,
            data: (_a = tab.filter) == null ? void 0 : _a.data,
            severity: (_b = tab.filter) == null ? void 0 : _b.severity
          };
          if (!checkNotificationMatchesFilter(notification, tabFilter)) {
            continue;
          }
          const filterKey = createCountsKey({
            tags: tabTags,
            data: (_c = tab.filter) == null ? void 0 : _c.data,
            severity: (_d = tab.filter) == null ? void 0 : _d.severity
          });
          if (!processedFilters.has(filterKey)) {
            processedFilters.add(filterKey);
            updateNewNotificationCountsOrCache(
              tab.label,
              notification,
              tabTags,
              (_e = tab.filter) == null ? void 0 : _e.data,
              (_f = tab.filter) == null ? void 0 : _f.severity
            );
          }
        }
      } else {
        updateNewNotificationCountsOrCache("", notification, [], void 0, void 0);
      }
      yield refreshCounts();
    }));
    return disposeRoot;
  });
  const activate = () => {
    subscribers += 1;
    if (subscribers === 1) {
      dispose = start();
    }
    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      subscribers -= 1;
      if (subscribers === 0) {
        dispose == null ? void 0 : dispose();
        dispose = void 0;
      }
    };
  };
  const resetNewNotificationCounts = (key) => {
    setNewNotificationCounts((oldMap) => {
      const newMap = new Map(oldMap);
      newMap.set(key, 0);
      return newMap;
    });
  };
  return { unreadCount, unreadCounts, newNotificationCounts, resetNewNotificationCounts, activate };
};
var selectNewMessagesCount = (store, filter) => {
  const key = createMemo(() => createCountsKey(filter()));
  const count = createMemo(() => store.newNotificationCounts().get(key()) || 0);
  const reset = () => store.resetNewNotificationCounts(key());
  return { count, reset };
};
var twMerge = extendTailwindMerge({
  prefix: "nt-"
});
var publicFacingTwMerge = extendTailwindMerge({});
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function generateRandomString(length) {
  const characters = "abcdefghijklmnopqrstuvwxyz";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
function generateUniqueRandomString(set, length) {
  let randomString;
  do {
    randomString = generateRandomString(length);
  } while (set.has(randomString));
  return randomString;
}

// src/ui/config/appearanceKeys.ts
var commonAppearanceKeys = [
  // Primitives
  "root",
  "button",
  "input",
  "icon",
  "badge",
  "popoverContent",
  "popoverTrigger",
  "popoverClose",
  "collapsible",
  "tooltipContent",
  "tooltipTrigger"
];
var inboxAppearanceKeys = [
  // General
  "bellIcon",
  "lockIcon",
  "bellContainer",
  "severityHigh__bellContainer",
  "severityMedium__bellContainer",
  "severityLow__bellContainer",
  "bellSeverityGlow",
  "severityGlowHigh__bellSeverityGlow",
  "severityGlowMedium__bellSeverityGlow",
  "severityGlowLow__bellSeverityGlow",
  "bellDot",
  "preferences__button",
  "preferencesContainer",
  "inboxHeader",
  "loading",
  "dropdownContent",
  "dropdownTrigger",
  "dropdownItem",
  "dropdownItemLabel",
  "dropdownItemLabelContainer",
  "dropdownItemLeft__icon",
  "dropdownItemRight__icon",
  "dropdownItem__icon",
  "datePicker",
  "datePickerGrid",
  "datePickerGridRow",
  "datePickerGridCell",
  "datePickerGridCellTrigger",
  "datePickerTrigger",
  "datePickerGridHeader",
  "datePickerControl",
  "datePickerControlPrevTrigger",
  "datePickerControlNextTrigger",
  "datePickerControlPrevTrigger__icon",
  "datePickerControlNextTrigger__icon",
  "datePickerCalendar",
  "datePickerHeaderMonth",
  "datePickerCalendarDay__button",
  "timePicker",
  "timePicker__hourSelect",
  "timePicker__minuteSelect",
  "timePicker__periodSelect",
  "timePicker__separator",
  "timePickerHour__input",
  "timePickerMinute__input",
  "snoozeDatePicker",
  "snoozeDatePicker__actions",
  "snoozeDatePickerCancel__button",
  "snoozeDatePickerApply__button",
  "snoozeDatePicker__timePickerContainer",
  "snoozeDatePicker__timePickerLabel",
  "back__button",
  "skeletonText",
  "skeletonAvatar",
  "skeletonSwitch",
  "skeletonSwitchThumb",
  "tabsRoot",
  "tabsList",
  "tabsContent",
  "tabsTrigger",
  "dots",
  // Inbox
  "inboxContent",
  "inbox__popoverTrigger",
  "inbox__popoverContent",
  // Notifications
  "notificationListContainer",
  "notificationList",
  "notificationListEmptyNoticeContainer",
  "notificationListEmptyNoticeOverlay",
  "notificationListEmptyNoticeIcon",
  "notificationListEmptyNotice",
  "notificationList__skeleton",
  "notificationList__skeletonContent",
  "notificationList__skeletonItem",
  "notificationList__skeletonAvatar",
  "notificationList__skeletonText",
  "notificationListNewNotificationsNotice__button",
  "notification",
  "severityHigh__notification",
  "severityMedium__notification",
  "severityLow__notification",
  "notificationBar",
  "severityHigh__notificationBar",
  "severityMedium__notificationBar",
  "severityLow__notificationBar",
  "notificationContent",
  "notificationTextContainer",
  "notificationDot",
  "notificationSubject",
  "notificationSubject__strong",
  "notificationSubject__em",
  "notificationBody",
  "notificationBody__strong",
  "notificationBody__em",
  "notificationBodyContainer",
  "notificationImage",
  "notificationImageLoadingFallback",
  "notificationDate",
  "notificationDateActionsContainer",
  "notificationDefaultActions",
  "notificationCustomActions",
  "notificationPrimaryAction__button",
  "notificationSecondaryAction__button",
  "notificationRead__button",
  "notificationUnread__button",
  "notificationArchive__button",
  "notificationUnarchive__button",
  "notificationSnooze__button",
  "notificationUnsnooze__button",
  "notificationRead__icon",
  "notificationUnread__icon",
  "notificationArchive__icon",
  "notificationUnarchive__icon",
  "notificationSnooze__icon",
  "notificationUnsnooze__icon",
  // Notifications tabs
  "notificationsTabs__tabsRoot",
  "notificationsTabs__tabsList",
  "notificationsTabs__tabsContent",
  "notificationsTabs__tabsTrigger",
  "notificationsTabsTriggerLabel",
  "notificationsTabsTriggerCount",
  // Inbox status
  "inboxStatus__title",
  "inboxStatus__dropdownTrigger",
  "inboxStatus__dropdownContent",
  "inboxStatus__dropdownItem",
  "inboxStatus__dropdownItemLabel",
  "inboxStatus__dropdownItemLabelContainer",
  "inboxStatus__dropdownItemLeft__icon",
  "inboxStatus__dropdownItemRight__icon",
  "inboxStatus__dropdownItem__icon",
  "inboxStatus__dropdownItemCheck__icon",
  // More actions
  "moreActionsContainer",
  "moreActions__dropdownTrigger",
  "moreActions__dropdownContent",
  "moreActions__dropdownItem",
  "moreActions__dropdownItemLabel",
  "moreActions__dropdownItemLeft__icon",
  "moreActions__dots",
  // More tabs
  "moreTabs__button",
  "moreTabs__icon",
  "moreTabs__dropdownTrigger",
  "moreTabs__dropdownContent",
  "moreTabs__dropdownItem",
  "moreTabs__dropdownItemLabel",
  "moreTabs__dropdownItemRight__icon",
  // workflow
  "workflowContainer",
  "workflowLabel",
  "workflowLabelHeader",
  "workflowLabelHeaderContainer",
  "workflowLabelIcon",
  "workflowLabelContainer",
  "workflowContainerDisabledNotice",
  "workflowLabelDisabled__icon",
  "workflowContainerRight__icon",
  "workflowArrow__icon",
  "workflowDescription",
  // preference groups
  "preferencesGroupContainer",
  "preferencesGroupHeader",
  "preferencesGroupLabelContainer",
  "preferencesGroupLabelIcon",
  "preferencesGroupLabel",
  "preferencesGroupActionsContainer",
  "preferencesGroupActionsContainerRight__icon",
  "preferencesGroupBody",
  "preferencesGroupChannels",
  "preferencesGroupInfo",
  "preferencesGroupInfoIcon",
  "preferencesGroupWorkflows",
  // channel
  "channelContainer",
  "channelIconContainer",
  "channel__icon",
  "channelsContainerCollapsible",
  "channelsContainer",
  "channelLabel",
  "channelLabelContainer",
  "channelName",
  "channelSwitchContainer",
  "channelSwitch",
  "channelSwitchThumb",
  // Preferences Header
  "preferencesHeader",
  "preferencesHeader__back__button",
  "preferencesHeader__back__button__icon",
  "preferencesHeader__title",
  "preferencesHeader__icon",
  // Preferences Loading
  "preferencesListEmptyNoticeContainer",
  "preferencesListEmptyNotice",
  "preferencesList__skeleton",
  "preferencesList__skeletonContent",
  "preferencesList__skeletonItem",
  "preferencesList__skeletonIcon",
  "preferencesList__skeletonSwitch",
  "preferencesList__skeletonSwitchThumb",
  "preferencesList__skeletonText",
  // Schedule
  "scheduleContainer",
  "scheduleHeader",
  "scheduleLabelContainer",
  "scheduleLabelScheduleIcon",
  "scheduleLabelInfoIcon",
  "scheduleLabel",
  "scheduleActionsContainer",
  "scheduleActionsContainerRight",
  "scheduleBody",
  "scheduleDescription",
  "scheduleTable",
  "scheduleTableHeader",
  "scheduleHeaderColumn",
  "scheduleTableBody",
  "scheduleBodyRow",
  "scheduleBodyColumn",
  "scheduleInfoContainer",
  "scheduleInfoIcon",
  "scheduleInfo",
  // Day Schedule Copy
  "dayScheduleCopyTitle",
  "dayScheduleCopyIcon",
  "dayScheduleCopySelectAll",
  "dayScheduleCopyDay",
  "dayScheduleCopyFooterContainer",
  "dayScheduleCopy__dropdownTrigger",
  "dayScheduleCopy__dropdownContent",
  // Time Select
  "timeSelect__dropdownTrigger",
  "timeSelect__time",
  "timeSelect__dropdownContent",
  "timeSelect__dropdownItem",
  "timeSelect__dropdownItemLabel",
  "timeSelect__dropdownItemLabelContainer",
  "timeSelect__dropdownItemCheck__icon",
  // Notification Snooze
  "notificationSnooze__dropdownContent",
  "notificationSnooze__dropdownItem",
  "notificationSnooze__dropdownItem__icon",
  "notificationSnoozeCustomTime_popoverContent",
  // Notification Delivered At
  "notificationDeliveredAt__badge",
  "notificationDeliveredAt__icon",
  "notificationSnoozedUntil__icon",
  // Text formatting
  "strong",
  "em"
];
var subscriptionAppearanceKeys = [
  // Subscription
  "subscriptionContainer",
  // Subscription Button
  "subscriptionButton__button",
  "subscriptionButtonContainer",
  "subscriptionButtonIcon",
  "subscriptionButtonLabel",
  // Subscription Popover
  "subscription__popoverTriggerContainer",
  "subscription__popoverTrigger",
  "subscriptionTriggerIcon",
  "subscription__popoverContent",
  // Subscription Preferences
  "subscriptionPreferencesContainer",
  "subscriptionPreferencesHeaderContainer",
  "subscriptionPreferencesHeader",
  "subscriptionPreferencesInfoIcon",
  "subscriptionPreferencesContent",
  "subscriptionPreferencesGroupsContainer",
  // Subscription Preferences Fallback
  "subscriptionPreferencesFallback",
  "subscriptionPreferencesFallbackTexts",
  "subscriptionPreferencesFallbackHeader",
  "subscriptionPreferencesFallbackDescription",
  // Subscription Preference Row
  "subscriptionPreferenceRow",
  "subscriptionPreferenceLabel",
  // Subscription Preference Group Row
  "subscriptionPreferenceGroupContainer",
  "subscriptionPreferenceGroupHeader",
  "subscriptionPreferenceGroupLabelContainer",
  "subscriptionPreferenceGroupLabelIcon",
  "subscriptionPreferenceGroupLabel",
  "subscriptionPreferenceGroupActionsContainer",
  "subscriptionPreferenceGroupActionsContainerRight__icon",
  "subscriptionPreferenceGroupBody",
  "subscriptionPreferenceGroupWorkflowRow",
  "subscriptionPreferenceGroupWorkflowLabel"
];
var connectChatAppearanceKeys = [
  "connectChatContainer",
  "connectChatButton",
  "connectChatButtonContainer",
  "connectChatButtonLabel",
  "connectChatMisconfiguredTooltip"
];
var channelConnectButtonAppearanceKeys = [
  "channelConnectButtonContainer",
  "channelConnectButton",
  "channelConnectButtonInner",
  "channelConnectButtonIcon",
  "channelConnectButtonLabel",
  "channelConnectButtonMisconfiguredTooltip"
];
var linkSlackUserAppearanceKeys = [
  "linkSlackUserContainer",
  "linkSlackUserButton",
  "linkSlackUserButtonContainer",
  "linkSlackUserButtonIcon",
  "linkSlackUserButtonLabel"
];
var linkMsTeamsUserAppearanceKeys = [
  "linkMsTeamsUserContainer",
  "linkMsTeamsUserButton",
  "linkMsTeamsUserButtonContainer",
  "linkMsTeamsUserButtonIcon",
  "linkMsTeamsUserButtonLabel"
];
var appearanceKeys = [
  ...commonAppearanceKeys,
  ...inboxAppearanceKeys,
  ...subscriptionAppearanceKeys,
  ...connectChatAppearanceKeys,
  ...linkSlackUserAppearanceKeys,
  ...linkMsTeamsUserAppearanceKeys,
  ...channelConnectButtonAppearanceKeys
];

// src/ui/core/style/resolveStyle.ts
var resolveStyle = ({ key, className, iconKey, context }, source) => {
  if (!key) {
    return cn(className);
  }
  const appearanceKeyParts = key.split("__");
  let finalAppearanceKeys = [];
  for (let i = 0; i < appearanceKeyParts.length; i += 1) {
    const accumulated = appearanceKeyParts.slice(i).join("__");
    if (appearanceKeys.includes(accumulated)) {
      finalAppearanceKeys.push(accumulated);
    }
  }
  const classes = (className == null ? void 0 : className.split(/\s+/).map((className2) => className2.replace(/^nv-/, ""))) || [];
  const appearanceKeysInClasses = classes.filter(
    (className2) => appearanceKeys.includes(className2)
  );
  finalAppearanceKeys = Array.from(
    /* @__PURE__ */ new Set([...finalAppearanceKeys, ...appearanceKeysInClasses])
  );
  finalAppearanceKeys.sort((a, b) => {
    const countA = (a.match(/__/g) || []).length;
    const countB = (b.match(/__/g) || []).length;
    return countB - countA;
  });
  const finalClassName = classes.filter((className2) => !finalAppearanceKeys.includes(className2)).join(" ");
  let appearanceClassnames = [];
  const reversedFinalAppearanceKeys = finalAppearanceKeys.reverse();
  for (let i = 0; i < reversedFinalAppearanceKeys.length; i += 1) {
    const elementStyles = source.elements[reversedFinalAppearanceKeys[i]];
    if (typeof elementStyles === "string") {
      appearanceClassnames.push(elementStyles);
    } else if (typeof elementStyles === "function") {
      appearanceClassnames.push(elementStyles(context));
    }
  }
  appearanceClassnames = [publicFacingTwMerge(appearanceClassnames)];
  const cssInJsClasses = !!finalAppearanceKeys.length && !source.isServer ? finalAppearanceKeys.map((appKey) => source.appearanceKeyToCssInJsClass[appKey]) : [];
  return cn(
    ...finalAppearanceKeys.map((key2) => `nv-${key2}`),
    "\u{1F514}",
    iconKey ? `nv-${iconKey} \u{1F5BC}\uFE0F` : "",
    finalClassName,
    // default styles
    appearanceClassnames,
    ...cssInJsClasses
  );
};

// src/ui/core/format/relativeTime.ts
var DEFAULT_LOCALE = "en-US";
var SECONDS = {
  inMinute: 60,
  inHour: 3600,
  inDay: 86400,
  inWeek: 604800,
  inMonth: 2592e3
};
function formatToRelativeTime({
  fromDate,
  locale = DEFAULT_LOCALE,
  toDate = /* @__PURE__ */ new Date()
}) {
  const intlLocale = normalizeIntlLocale(locale);
  const elapsed = toDate.getTime() - fromDate.getTime();
  const formatter = new Intl.RelativeTimeFormat(intlLocale, { style: "narrow" });
  const diffInSeconds = Math.floor(elapsed / 1e3);
  if (Math.abs(diffInSeconds) < SECONDS.inMinute) {
    const subMinuteFormatter = new Intl.RelativeTimeFormat(intlLocale, { style: "narrow", numeric: "auto" });
    return subMinuteFormatter.format(-0, "second");
  } else if (Math.abs(diffInSeconds) < SECONDS.inHour) {
    return formatter.format(-Math.floor(diffInSeconds / SECONDS.inMinute), "minute");
  } else if (Math.abs(diffInSeconds) < SECONDS.inDay) {
    return formatter.format(-Math.floor(diffInSeconds / SECONDS.inHour), "hour");
  } else if (Math.abs(diffInSeconds) < SECONDS.inMonth) {
    return formatter.format(-Math.floor(diffInSeconds / SECONDS.inDay), "day");
  } else {
    return new Intl.DateTimeFormat(intlLocale, { month: "short", day: "numeric" }).format(fromDate);
  }
}
function formatSnoozedUntil({ untilDate, locale = DEFAULT_LOCALE }) {
  const intlLocale = normalizeIntlLocale(locale);
  const remaining = untilDate.getTime() - (/* @__PURE__ */ new Date()).getTime();
  const diffInSeconds = Math.floor(remaining / 1e3);
  if (diffInSeconds < 0) {
    return "soon";
  }
  if (diffInSeconds < SECONDS.inMinute) {
    return "a moment";
  } else if (diffInSeconds < SECONDS.inHour) {
    const minutes = Math.floor(diffInSeconds / SECONDS.inMinute);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  } else if (diffInSeconds < SECONDS.inDay) {
    const hours = Math.floor(diffInSeconds / SECONDS.inHour);
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  } else if (diffInSeconds < SECONDS.inWeek) {
    const days = Math.floor(diffInSeconds / SECONDS.inDay);
    return `${days} ${days === 1 ? "day" : "days"}`;
  } else {
    return new Intl.DateTimeFormat(intlLocale, { month: "short", day: "numeric" }).format(untilDate);
  }
}

// src/ui/core/style/css.ts
function cssObjectToString(styles) {
  return Object.entries(styles).map(([key, value]) => {
    const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    return `${kebabKey}: ${value};`;
  }).join(" ");
}
function createClassAndRuleFromCssString(classNameSet, styles) {
  const className = `novu-css-${generateUniqueRandomString(classNameSet, 8)}`;
  const rule = `.${className} { ${styles} }`;
  classNameSet.add(className);
  return { className, rule };
}
var shades = [25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
function generateDefaultColor(props) {
  const cssVariableDefaultRule = `.${props.id} { --nv-${props.key}: oklch(from ${props.color} l c h); }`;
  return cssVariableDefaultRule;
}
function generateSolidShadeRulesFromColor({ color, key, id }) {
  const rules = [];
  const adjustLightness = (factor) => {
    if (factor >= 0) {
      return `min(1, calc(l + ${factor} * (1 - l)))`;
    } else {
      return `max(0, calc(l * (1 + ${factor})))`;
    }
  };
  const lightnessOffsets = {
    25: adjustLightness(0.475),
    50: adjustLightness(0.45),
    100: adjustLightness(0.4),
    200: adjustLightness(0.3),
    300: adjustLightness(0.2),
    400: adjustLightness(0.1),
    500: "l",
    600: adjustLightness(-0.1),
    700: adjustLightness(-0.2),
    800: adjustLightness(-0.3),
    900: adjustLightness(-0.4)
  };
  shades.forEach((shade) => {
    const newLightness = lightnessOffsets[shade];
    const cssVariableRule = `.${id} { --nv-${key}-${shade}: oklch(from ${color} ${newLightness} c h); }`;
    rules.push(cssVariableRule);
  });
  return rules;
}
function generateAlphaShadeRulesFromColor({ color, key, id }) {
  const rules = [];
  const alphaMap = {
    25: 0.025,
    50: 0.05,
    100: 0.1,
    200: 0.2,
    300: 0.3,
    400: 0.4,
    500: 0.5,
    600: 0.6,
    700: 0.7,
    800: 0.8,
    900: 0.9
  };
  Object.entries(alphaMap).forEach(([shade, alpha]) => {
    const cssVariableAlphaRule = `.${id} { --nv-${key}-${shade}: oklch(from ${color} l c h / ${alpha}); }`;
    rules.push(cssVariableAlphaRule);
  });
  return rules;
}
function generateFontSizeRules(props) {
  const { id, baseFontSize } = props;
  const sizeRatios = {
    xs: 0.65625,
    sm: 0.765625,
    base: 0.875,
    lg: 0.984375,
    xl: 1.09375,
    "2xl": 1.3125,
    "3xl": 1.640625,
    "4xl": 1.96875
  };
  const rules = [];
  Object.entries(sizeRatios).forEach(([key, ratio]) => {
    const size = `calc(${baseFontSize} * ${ratio})`;
    const lineHeight = `calc(${baseFontSize} * ${ratio} * 1.33)`;
    const cssVariableRule = `.${id} { --nv-font-size-${key}: ${size}; --nv-line-height-${key}: ${lineHeight}; }`;
    rules.push(cssVariableRule);
  });
  return rules;
}
function generateBorderRadiusRules(props) {
  const { id, baseRadius } = props;
  const radiusRatios = {
    none: 0,
    xs: 0.333,
    sm: 0.667,
    md: 1,
    lg: 1.333,
    xl: 2,
    "2xl": 2.667,
    "3xl": 4,
    full: 9999
  };
  const rules = [];
  Object.entries(radiusRatios).forEach(([key, ratio]) => {
    const value = key === "none" ? "0px" : key === "full" ? "9999px" : `calc(${baseRadius} * ${ratio})`;
    const cssVariableRule = `.${id} { --nv-radius-${key}: ${value}; }`;
    rules.push(cssVariableRule);
  });
  return rules;
}
var parseVariables = (variables, id) => {
  const rules = [
    generateDefaultColor({ color: variables.colorBackground, key: "color-background", id }),
    generateDefaultColor({ color: variables.colorForeground, key: "color-foreground", id }),
    generateDefaultColor({ color: variables.colorPrimary, key: "color-primary", id }),
    generateDefaultColor({ color: variables.colorPrimaryForeground, key: "color-primary-foreground", id }),
    generateDefaultColor({ color: variables.colorSecondary, key: "color-secondary", id }),
    generateDefaultColor({ color: variables.colorSecondaryForeground, key: "color-secondary-foreground", id }),
    generateDefaultColor({ color: variables.colorCounter, key: "color-counter", id }),
    generateDefaultColor({ color: variables.colorCounterForeground, key: "color-counter-foreground", id }),
    generateDefaultColor({ color: variables.colorShadow, key: "color-shadow", id }),
    generateDefaultColor({ color: variables.colorRing, key: "color-ring", id }),
    generateDefaultColor({ color: variables.colorStripes, key: "color-stripes", id }),
    generateDefaultColor({ color: variables.colorSeverityHigh, key: "color-severity-high", id }),
    generateDefaultColor({ color: variables.colorSeverityMedium, key: "color-severity-medium", id }),
    generateDefaultColor({ color: variables.colorSeverityLow, key: "color-severity-low", id }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorSeverityHigh, key: "color-severity-high-alpha", id }),
    ...generateAlphaShadeRulesFromColor({
      color: variables.colorSeverityMedium,
      key: "color-severity-medium-alpha",
      id
    }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorSeverityLow, key: "color-severity-low-alpha", id }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorBackground, key: "color-background-alpha", id }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorForeground, key: "color-foreground-alpha", id }),
    ...generateSolidShadeRulesFromColor({ color: variables.colorPrimary, key: "color-primary", id }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorPrimary, key: "color-primary-alpha", id }),
    ...generateAlphaShadeRulesFromColor({
      color: variables.colorPrimaryForeground,
      key: "color-primary-foreground-alpha",
      id
    }),
    ...generateSolidShadeRulesFromColor({ color: variables.colorSecondary, key: "color-secondary", id }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorSecondary, key: "color-secondary-alpha", id }),
    ...generateAlphaShadeRulesFromColor({
      color: variables.colorSecondaryForeground,
      key: "color-secondary-foreground-alpha",
      id
    }),
    ...generateAlphaShadeRulesFromColor({ color: variables.colorNeutral, key: "color-neutral-alpha", id }),
    ...generateFontSizeRules({ id, baseFontSize: variables.fontSize }),
    ...generateBorderRadiusRules({ id, baseRadius: variables.borderRadius })
  ];
  return rules;
};
var parseElements = (elements) => {
  const elementsStyleData = [];
  const generatedClassNames = /* @__PURE__ */ new Set();
  for (const key in elements) {
    if (elements.hasOwnProperty(key)) {
      const value = elements[key];
      if (typeof value === "object") {
        const cssString = cssObjectToString(value);
        const { className, rule } = createClassAndRuleFromCssString(generatedClassNames, cssString);
        elementsStyleData.push({ key, rule, className });
      }
    }
  }
  const sortedElementsStyleData = elementsStyleData.sort((a, b) => {
    const countA = (a.key.match(/__/g) || []).length;
    const countB = (b.key.match(/__/g) || []).length;
    return countA - countB;
  });
  return sortedElementsStyleData;
};
var NOVU_DEFAULT_CSS_ID = "novu-default-css";

// src/ui/core/item/controller.ts
var ISLAND_ATTRIBUTE = "data-novu-island";
var isInsideIsland = (target) => {
  return target instanceof Element && target.closest(`[${ISLAND_ATTRIBUTE}]`) !== null;
};
var createNotificationItemController = ({
  notification,
  handlers,
  navigate
}) => {
  const isClickable = () => {
    var _a;
    const current = notification();
    return !current.isRead || !!((_a = current.redirect) == null ? void 0 : _a.url);
  };
  const handleClick = (event) => __async(null, null, function* () {
    var _a, _b, _c, _d;
    if (isInsideIsland(event.target)) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const current = notification();
    if (!current.isRead) {
      yield current.read();
    }
    (_b = (_a = handlers()).onNotificationClick) == null ? void 0 : _b.call(_a, current);
    navigate((_c = current.redirect) == null ? void 0 : _c.url, (_d = current.redirect) == null ? void 0 : _d.target);
  });
  const handleActionClick = (action, event) => __async(null, null, function* () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
    event.stopPropagation();
    const current = notification();
    if (action === "primary" /* PRIMARY */) {
      yield current.completePrimary();
      (_b = (_a = handlers()).onPrimaryActionClick) == null ? void 0 : _b.call(_a, current);
      navigate((_d = (_c = current.primaryAction) == null ? void 0 : _c.redirect) == null ? void 0 : _d.url, (_f = (_e = current.primaryAction) == null ? void 0 : _e.redirect) == null ? void 0 : _f.target);
    } else {
      yield current.completeSecondary();
      (_h = (_g = handlers()).onSecondaryActionClick) == null ? void 0 : _h.call(_g, current);
      navigate((_j = (_i = current.secondaryAction) == null ? void 0 : _i.redirect) == null ? void 0 : _j.url, (_l = (_k = current.secondaryAction) == null ? void 0 : _k.redirect) == null ? void 0 : _l.target);
    }
  });
  return { isClickable, handleClick, handleActionClick };
};

// src/ui/core/style/tables.ts
var SEVERITY_TO_NOTIFICATION_KEYS = {
  ["none" /* NONE */]: "notification",
  ["high" /* HIGH */]: "severityHigh__notification",
  ["medium" /* MEDIUM */]: "severityMedium__notification",
  ["low" /* LOW */]: "severityLow__notification"
};
var SEVERITY_TO_BAR_KEYS = {
  ["none" /* NONE */]: "notificationBar",
  ["high" /* HIGH */]: "severityHigh__notificationBar",
  ["medium" /* MEDIUM */]: "severityMedium__notificationBar",
  ["low" /* LOW */]: "severityLow__notificationBar"
};
var notificationItemStyles = {
  root: {
    className: "nt-transition nt-w-full nt-text-sm hover:nt-bg-primary-alpha-25 nt-group nt-relative nt-flex nt-items-start nt-p-4 nt-gap-2 [&:not(:first-child)]:nt-border-t nt-border-neutral-alpha-100",
    clickable: "nt-cursor-pointer",
    severity: {
      ["none" /* NONE */]: "",
      ["high" /* HIGH */]: "nt-bg-severity-high-alpha-100 hover:nt-bg-severity-high-alpha-50",
      ["medium" /* MEDIUM */]: "nt-bg-severity-medium-alpha-100 hover:nt-bg-severity-medium-alpha-50",
      ["low" /* LOW */]: "nt-bg-severity-low-alpha-100 hover:nt-bg-severity-low-alpha-50"
    }
  },
  bar: {
    className: "nt-transition nt-absolute nt-left-0 nt-top-0 nt-bottom-0 nt-w-[3px]",
    severity: {
      ["none" /* NONE */]: "",
      ["high" /* HIGH */]: "nt-bg-severity-high group-hover:nt-bg-severity-high-alpha-500",
      ["medium" /* MEDIUM */]: "nt-bg-severity-medium group-hover:nt-bg-severity-medium-alpha-500",
      ["low" /* LOW */]: "nt-bg-severity-low group-hover:nt-bg-severity-low-alpha-500"
    }
  },
  avatarFallback: {
    key: "notificationImageLoadingFallback",
    className: "nt-size-8 nt-rounded-lg nt-shrink-0 nt-aspect-square"
  },
  avatar: {
    key: "notificationImage",
    className: "nt-size-8 nt-rounded-lg nt-object-cover nt-aspect-square"
  },
  content: {
    key: "notificationContent",
    className: "nt-flex nt-flex-col nt-gap-2 nt-w-full"
  },
  textContainer: {
    key: "notificationTextContainer",
    className: ""
  },
  subject: {
    key: "notificationSubject",
    strongKey: "notificationSubject__strong",
    emKey: "notificationSubject__em",
    className: "nt-text-start nt-font-medium nt-whitespace-pre-wrap [word-break:break-word]"
  },
  body: {
    key: "notificationBody",
    strongKey: "notificationBody__strong",
    emKey: "notificationBody__em",
    className: "nt-text-start nt-whitespace-pre-wrap nt-text-foreground-alpha-600 [word-break:break-word]"
  },
  defaultActions: {
    key: "notificationDefaultActions",
    className: "nt-absolute nt-transition nt-duration-100 nt-ease-out nt-gap-0.5 nt-flex nt-shrink-0 nt-opacity-0 group-hover:nt-opacity-100 group-focus-within:nt-opacity-100 nt-justify-center nt-items-center nt-bg-background/90 nt-right-3 nt-top-3 nt-border nt-border-neutral-alpha-100 nt-rounded-lg nt-backdrop-blur-lg nt-p-0.5"
  },
  customActions: {
    key: "notificationCustomActions",
    className: "nt-flex nt-flex-wrap nt-gap-2",
    primaryKey: "notificationPrimaryAction__button",
    secondaryKey: "notificationSecondaryAction__button"
  },
  date: {
    key: "notificationDate",
    className: "nt-text-foreground-alpha-400 nt-flex nt-items-center nt-gap-1"
  },
  deliveredAtBadge: {
    key: "notificationDeliveredAt__badge"
  },
  deliveredAtIcon: {
    key: "notificationDeliveredAt__icon",
    className: "nt-size-3"
  },
  snoozedUntilIcon: {
    key: "notificationSnoozedUntil__icon",
    className: "nt-size-3"
  },
  dotContainer: {
    className: "nt-w-1.5 nt-flex nt-justify-center nt-shrink-0"
  },
  dot: {
    key: "notificationDot",
    className: "nt-size-1.5 nt-bg-primary nt-rounded-full"
  }
};
var badgeStyles = {
  key: "badge",
  base: "nt-inline-flex nt-flex-row nt-gap-1 nt-items-center",
  variants: {
    secondary: "nt-bg-neutral-alpha-50"
  },
  sizes: {
    default: "nt-px-1 nt-py-px nt-rounded-sm nt-text-xs nt-px-1"
  }
};
var markdownStyles = {
  strong: { key: "strong", className: "nt-font-semibold" },
  em: { key: "em", className: "nt-italic" }
};
var rootDocument = (container) => container instanceof ShadowRoot ? container : document;
var injectDefaultCss = ({ css, container }) => {
  const stylesContainer = untrack(container);
  const root = rootDocument(stylesContainer);
  if (root.getElementById(NOVU_DEFAULT_CSS_ID)) {
    return;
  }
  const styleEl = document.createElement("style");
  styleEl.id = NOVU_DEFAULT_CSS_ID;
  styleEl.innerHTML = css;
  const parent = stylesContainer != null ? stylesContainer : document.head;
  parent.insertBefore(styleEl, parent.firstChild);
  onCleanup(() => {
    styleEl.remove();
  });
};
var createAppearanceStylesheet = ({
  id,
  container,
  rules
}) => {
  const [styleElement, setStyleElement] = createSignal(null);
  const stylesContainer = untrack(container);
  const root = rootDocument(stylesContainer);
  const existing = root.getElementById(id);
  if (existing) {
    setStyleElement(existing);
  } else {
    const parent = stylesContainer != null ? stylesContainer : document.head;
    const styleEl = document.createElement("style");
    styleEl.id = id;
    const defaultCssStyles = root.getElementById(NOVU_DEFAULT_CSS_ID);
    if (defaultCssStyles) {
      parent.insertBefore(styleEl, defaultCssStyles.nextSibling);
    } else {
      parent.appendChild(styleEl);
    }
    setStyleElement(styleEl);
    onCleanup(() => {
      styleEl.remove();
    });
  }
  createEffect(() => {
    const styleEl = styleElement();
    if (!styleEl) {
      return;
    }
    styleEl.innerHTML = rules().join(" ");
  });
};

// src/ui/config/defaultVariables.ts
var defaultVariables = {
  colorPrimary: "#7D52F4",
  colorPrimaryForeground: "white",
  colorSecondary: "#FFFFFF",
  colorSecondaryForeground: "#646464",
  colorCounter: "#FB3748",
  colorCounterForeground: "white",
  colorBackground: "#FCFCFC",
  colorRing: "#E1E4EA",
  colorForeground: "#1A1523",
  colorNeutral: "#525252",
  colorShadow: "rgb(0,0,0)",
  fontSize: "1rem",
  borderRadius: "0.375rem",
  colorStripes: "#FF9A68",
  colorSeverityHigh: "#FB3748",
  colorSeverityMedium: "#FF8447",
  colorSeverityLow: "transparent"
};

// src/ui/core/stores/appearance.ts
var EMPTY_VARIABLES = {};
var EMPTY_ICONS = {};
var createAppearanceStore = ({
  id,
  appearance,
  container,
  defaultCss
}) => {
  const themes = createMemo(() => {
    var _a;
    const baseTheme = (_a = appearance()) == null ? void 0 : _a.baseTheme;
    return Array.isArray(baseTheme) ? baseTheme : [baseTheme || {}];
  });
  const variables = createMemo(() => {
    var _a, _b;
    return (_b = (_a = appearance()) == null ? void 0 : _a.variables) != null ? _b : EMPTY_VARIABLES;
  });
  const animations = createMemo(() => {
    var _a, _b;
    return (_b = (_a = appearance()) == null ? void 0 : _a.animations) != null ? _b : true;
  });
  const icons = createMemo(() => {
    var _a, _b;
    return (_b = (_a = appearance()) == null ? void 0 : _a.icons) != null ? _b : EMPTY_ICONS;
  });
  const elements = createMemo(() => {
    var _a;
    const baseElements = themes().reduce((acc, obj) => __spreadValues(__spreadValues({}, acc), obj.elements || {}), {});
    return __spreadValues(__spreadValues({}, baseElements), ((_a = appearance()) == null ? void 0 : _a.elements) || {});
  });
  const variableRules = createMemo(() => {
    const baseVariables = __spreadValues(__spreadValues({}, defaultVariables), themes().reduce((acc, obj) => __spreadValues(__spreadValues({}, acc), obj.variables || {}), {}));
    return parseVariables(__spreadValues(__spreadValues({}, baseVariables), variables()), id);
  });
  const elementsStyleData = createMemo(() => parseElements(elements()));
  const elementRules = createMemo(() => elementsStyleData().map((el) => el.rule));
  const appearanceKeyToCssInJsClass = createMemo(
    () => elementsStyleData().reduce((acc, item) => {
      acc[item.key] = item.className;
      return acc;
    }, {})
  );
  if (defaultCss !== void 0) {
    injectDefaultCss({ css: defaultCss, container });
  }
  createAppearanceStylesheet({ id, container, rules: () => [...variableRules(), ...elementRules()] });
  return {
    id: () => id,
    container,
    variables,
    elements,
    animations,
    icons,
    appearanceKeyToCssInJsClass
  };
};

export { DEFAULT_LIMIT, ISLAND_ATTRIBUTE, NOVU_DEFAULT_CSS_ID, SEVERITY_TO_BAR_KEYS, SEVERITY_TO_NOTIFICATION_KEYS, badgeStyles, cn, createAppearanceStore, createAppearanceStylesheet, createCountsKey, createCountsStore, createInboxStore, createLocalizationStore, createNavigate, createNotificationItemController, createNovuEventEffect, createWebSocketEventEffect, formatSnoozedUntil, formatToRelativeTime, generateRandomString, getTagsFromTab, injectDefaultCss, isInsideIsland, isWebLocksSupported, markdownStyles, normalizeIntlLocale, notificationItemStyles, parseElements, parseVariables, publicFacingTwMerge, requestLock, resolveStyle, selectNewMessagesCount, setDynamicLocalization };
